/**
 * CareStart Studio — list of facility variants with deactivate (no delete).
 *
 * WHAT: Shows deployed variants for the supervisor's linked facility.
 * WHY: Clinic Managers manage multiple named screenings from one page.
 */

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { updateCareStartVariant } from '../../../services/carestart/careStartVariantService';
import { contentCardSx } from '../../../styles/standaloneLayoutTokens';
import CareStartVariantPromptEditor from './CareStartVariantPromptEditor';

function buildShareUrl(variantSlug) {
  if (typeof window === 'undefined') return `/carestart/${variantSlug}`;
  return `${window.location.origin}/carestart/${variantSlug}`;
}

export default function CareStartVariantList({ variants, facilityId, userData, onChanged, promptDefaults }) {
  const [busySlug, setBusySlug] = useState(null);
  const [error, setError] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [expandedPromptSlug, setExpandedPromptSlug] = useState(null);

  const handleCopy = useCallback(async (slug) => {
    const url = buildShareUrl(slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      window.setTimeout(() => setCopiedSlug(null), 2000);
    } catch (err) {
      console.warn('[CareStartVariantList] copy failed', err);
    }
  }, []);

  const handleDeactivate = useCallback(
    async (slug) => {
      setBusySlug(slug);
      setError(null);
      try {
        await updateCareStartVariant(slug, { active: false }, userData);
        onChanged?.();
      } catch (err) {
        setError(err?.message || 'Could not deactivate this variant.');
      } finally {
        setBusySlug(null);
      }
    },
    [userData, onChanged]
  );

  const handleReactivate = useCallback(
    async (slug) => {
      setBusySlug(slug);
      setError(null);
      try {
        await updateCareStartVariant(slug, { active: true }, userData);
        onChanged?.();
      } catch (err) {
        setError(err?.message || 'Could not reactivate this variant.');
      } finally {
        setBusySlug(null);
      }
    },
    [userData, onChanged]
  );

  if (!facilityId) return null;

  if (!variants?.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        You have not created any CareStart variants yet. Use the form below to create your first
        shareable screening link.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {variants.map((v) => {
        const slug = v.variantSlug || v.id;
        const shareUrl = buildShareUrl(slug);
        const isActive = v.active !== false;
        const overrideCount = Object.entries(v.promptOverrides || {}).filter(
          ([, text]) => typeof text === 'string' && text.trim().length > 0
        ).length;
        return (
          <Paper key={slug} elevation={0} sx={{ ...contentCardSx, p: 2 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {v.displayName || slug}
                  </Typography>
                  <Chip
                    size="small"
                    label={isActive ? 'Active' : 'Inactive'}
                    color={isActive ? 'success' : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                  />
                  <Chip
                    size="small"
                    label={v.audienceMode === 'staff' ? 'Staff-assisted' : 'Patient self-service'}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={
                      overrideCount > 0
                        ? `${overrideCount} prompt override${overrideCount === 1 ? '' : 's'}`
                        : 'No prompt overrides'
                    }
                    color={overrideCount > 0 ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                  <LinkIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {shareUrl}
                  </Typography>
                  <Tooltip title={copiedSlug === slug ? 'Copied!' : 'Copy link'}>
                    <IconButton size="small" onClick={() => handleCopy(slug)} aria-label="Copy link">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {v.checkInEnabled ? (
                  <Chip size="small" label="Offered at check-in" color="primary" variant="outlined" />
                ) : null}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant={expandedPromptSlug === slug ? 'contained' : 'outlined'}
                    startIcon={<EditNoteIcon />}
                    onClick={() =>
                      setExpandedPromptSlug((prev) => (prev === slug ? null : slug))
                    }
                  >
                    Edit prompts
                  </Button>
                  {isActive ? (
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    disabled={busySlug === slug}
                    onClick={() => handleDeactivate(slug)}
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busySlug === slug}
                    onClick={() => handleReactivate(slug)}
                  >
                    Reactivate
                  </Button>
                )}
                </Stack>
              </Box>
            </Stack>
            <Collapse in={expandedPromptSlug === slug}>
              <CareStartVariantPromptEditor
                variant={v}
                defaults={promptDefaults}
                userData={userData}
                onSaved={onChanged}
              />
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}

CareStartVariantList.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.object),
  facilityId: PropTypes.string,
  userData: PropTypes.object,
  onChanged: PropTypes.func,
  promptDefaults: PropTypes.arrayOf(PropTypes.object),
};
